import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link, useSearchParams, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Unit, Lesson, LessonFormData } from '@/types'
import {
  ArrowLeft,
  Save,
  BookOpen,
  Layers,
  Edit
} from 'lucide-react'

export default function LessonForm() {
  const { lessonId } = useParams()
  const [searchParams] = useSearchParams()
  const unitIdFromUrl = searchParams.get('unit')
  const navigate = useNavigate()
  const location = useLocation()
  const isEditing = Boolean(lessonId) && lessonId !== 'new'
  const isViewMode = isEditing && !location.pathname.endsWith('/edit')

  const [units, setUnits] = useState<Unit[]>([])
  const [formData, setFormData] = useState<LessonFormData>({
    unit_id: '',
    title: '',
    description: '',
    order_num: 1,
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadUnits()
    if (isEditing) {
      loadLesson()
    }
  }, [lessonId])

  const loadUnits = async () => {
    try {
      const { data, error } = await supabase
        .from('units')
        .select('*')
        .order('order_num', { ascending: true })

      if (error) throw error
      const unitsData = data as unknown as Unit[]
      setUnits(unitsData || [])

      // Si no estamos editando, usar la unidad de la URL o la primera
      if (!isEditing && unitsData && unitsData.length > 0 && !formData.unit_id) {
        const selectedUnitId = unitIdFromUrl || unitsData[0].id
        setFormData(prev => ({ ...prev, unit_id: selectedUnitId }))
        loadNextOrderNum(selectedUnitId)
      }
    } catch (error) {
      console.error('Error loading units:', error)
    }
  }

  const loadLesson = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('id', lessonId as string)
        .single()

      if (error) throw error

      const lessonData = data as unknown as Lesson

      setFormData({
        unit_id: lessonData.unit_id,
        title: lessonData.title,
        description: lessonData.description || '',
        order_num: lessonData.order_num,
        is_active: lessonData.is_active
      })
    } catch (error) {
      console.error('Error loading lesson:', error)
      setError('Error al cargar la lección')
    } finally {
      setIsLoading(false)
    }
  }

  const loadNextOrderNum = async (unitId: string) => {
    try {
      const { data } = await supabase
        .from('lessons')
        .select('order_num')
        .eq('unit_id', unitId)
        .order('order_num', { ascending: false })
        .limit(1)

      const orderData = data as unknown as { order_num: number }[]
      if (orderData && orderData.length > 0) {
        setFormData(prev => ({ ...prev, order_num: orderData[0].order_num + 1 }))
      } else {
        setFormData(prev => ({ ...prev, order_num: 1 }))
      }
    } catch (error) {
      console.error('Error loading order num:', error)
    }
  }

  const handleUnitChange = (unitId: string) => {
    setFormData(prev => ({ ...prev, unit_id: unitId }))
    if (!isEditing) {
      loadNextOrderNum(unitId)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSaving(true)

    try {
      if (isEditing) {
        const { error } = await (supabase
          .from('lessons') as any)
          .update({
            unit_id: formData.unit_id,
            title: formData.title,
            description: formData.description,
            order_num: formData.order_num,
            is_active: formData.is_active,
            updated_at: new Date().toISOString()
          })
          .eq('id', lessonId as string)

        if (error) throw error
      } else {
        const { error } = await (supabase
          .from('lessons') as any)
          .insert({
            unit_id: formData.unit_id,
            title: formData.title,
            description: formData.description,
            order_num: formData.order_num,
            is_active: formData.is_active
          })

        if (error) throw error
      }

      navigate('/admin/lessons')
    } catch (error: any) {
      console.error('Error saving lesson:', error)
      setError(error.message || 'Error al guardar la lección')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/lessons"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isViewMode ? 'Visualizar Lección' : (isEditing ? 'Editar Lección' : 'Nueva Lección')}
            </h1>
            <p className="text-gray-600">
              {isViewMode ? 'Vista previa de la lección' : (isEditing ? 'Modifica los datos de la lección' : 'Crea una nueva lección')}
            </p>
          </div>
        </div>
        {isViewMode && (
          <Link
            to={`/admin/lessons/${lessonId}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Edit className="w-5 h-5" />
            Editar
          </Link>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Unit Selection */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-green-100">
              <Layers className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Unidad</h2>
          </div>

          <div>
            <label htmlFor="lesson-unit" className="block text-sm font-medium text-gray-700 mb-1">
              Selecciona la Unidad
            </label>
            <select id="lesson-unit" value={formData.unit_id}
              onChange={(e) => handleUnitChange(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
              disabled={isViewMode}
            >
              <option value="">Seleccionar unidad...</option>
              {units.map(unit => (
                <option key={unit.id} value={unit.id}>
                  Unidad {unit.order_num}: {unit.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-[#0055A4]/10">
              <BookOpen className="w-5 h-5 text-[#0055A4]" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Información de la Lección</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="lesson-order" className="block text-sm font-medium text-gray-700 mb-1">
                Orden
              </label>
              <input id="lesson-order" type="number"
                min="1"
                value={formData.order_num}
                onChange={(e) => setFormData(prev => ({ ...prev, order_num: Number.parseInt(e.target.value) || 1 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              />
            </div>
            <div className="md:col-span-3">
              <label htmlFor="lesson-title" className="block text-sm font-medium text-gray-700 mb-1">
                Título de la Lección
              </label>
              <input
                id="lesson-title"
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ej: Lección 1 - Saludos básicos"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              />
            </div>
          </div>

          <div>
            <label htmlFor="lesson-description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              id="lesson-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe el contenido de esta lección..."
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              className="w-4 h-4 text-[#0055A4] border-gray-300 rounded focus:ring-[#0055A4] disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Lección activa (visible para estudiantes)
            </label>
          </div>
        </div>

        {/* Actions */}
        {!isViewMode && (
          <div className="flex justify-end gap-3">
            <Link
              to="/admin/lessons"
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={isSaving || !formData.unit_id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Lección')}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}
